import AuthService from '../services/auth-service.js';
import { getAccountFromLocalStorage } from '../services/http-service.js';

const accountFromStorage = getAccountFromLocalStorage();
const initialState = accountFromStorage
  ? { status: { loggedIn: true }, account: accountFromStorage }
  : { status: { loggedIn: false }, account: null };

export const auth = {
  namespaced: true,
  state: initialState,
  actions: {
    retrofitLogin({ commit }, payload) {
      commit('loginSuccess', payload);
    },
    login({ commit }, user) {
      return AuthService.login(user).then(
        (loginResult) => {
          if (loginResult.model.deviceValid) {
            AuthService.getAppData().then((resp) => {
              commit('setAppData', resp);
            });
            commit('loginSuccess', loginResult);
            return Promise.resolve(loginResult);
          }
          commit('setSmsCode', 'required');
          return Promise.resolve(loginResult);
        },
        (error) => {
          commit('loginFailure');
          if (error.response && error.response.status === 403) {
            throw new Error('You do not have permission to log in.');
          }
          return Promise.reject(error);
        }
      );
    },
    smsRequestCode({ commit }, payload) {
      return AuthService.sendSms(payload)
        .then((resp) => {
          if (resp.data.isSuccess) {
            commit('setSmsCode', 'requested');
            return Promise.resolve();
          }
          throw new Error('Failed to request an SMS');
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
    smsVerifyCode({ commit }, payload) {
      return AuthService.verifySms(payload)
        .then((resp) => {
          if (resp.data.isSuccess) {
            commit('setIsBusy', true, {
              root: true,
            });
            commit('setSmsCode', 'verified');
            return Promise.resolve();
          }
          throw new Error('Failed to verify SMS');
        })
        .catch((err) => {
          commit('setIsBusy', false, {
            root: true,
          });
          if (err.response.data.message) {
            return Promise.reject(err.response.data.message);
          }
          return Promise.reject(err);
        });
    },
    changeMobile({ commit }, payload) {
      return AuthService.changeMobile(payload)
        .then((resp) => {
          if (resp.data.isSuccess) {
            commit('setIsBusy', false, { root: true });
            commit('setSmsCode', 'requested');
            return Promise.resolve();
          }
          throw new Error('Failed to change your Mobile Number');
        })
        .catch((err) => {
          if (err.response.data.message) {
            return Promise.reject(err.response.data.message);
          }
          return Promise.reject(err);
        });
    },
    sendEmailVerifyCode({ commit }, payload) {
      return AuthService.sendEmailVerifyCode(payload)
        .then((resp) => {
          if (resp.data.isSuccess) {
            commit('setIsBusy', false, { root: true });
            commit('setVerificationCode', 'requested');
            return Promise.resolve();
          }
          throw new Error('Failed to change your Mobile Number');
        })
        .catch((err) => {
          if (err.response.data.message) {
            return Promise.reject(err.response.data.message);
          }
          return Promise.reject(err);
        });
    },
    logout({ commit }) {
      AuthService.logout();
      commit('logout');
    },
    getAppData({ commit, dispatch }) {
      commit('setIsBusy', true, { root: true });
      return AuthService.getAppData()
        .then((resp) => {
          commit('setAppData', resp);
          commit('setIsBusy', false, { root: true });
          dispatch('pendingLicenceAction/getBadge', {}, { root: true });
          dispatch('disputeSearch/getFollowUpDateCount', {}, { root: true });
          dispatch('brandmisuseSearch/getBrandmisuseFollowUpDateCount', {}, { root: true });
          return Promise.resolve(resp);
        })
        .catch((err) => {
          if (err.response && err.response.status === 403) {
            dispatch('logout');
          }
        });
    },
    register({ commit }, user) {
      return AuthService.register(user).then(
        (response) => {
          commit('registerSuccess');
          return Promise.resolve(response.data);
        },
        (error) => {
          if (error.response.status === 422) {
            if (error.response.data.reasons) {
              const reasons = error.response.data.reasons
                .map((x) => `${x.field} ${x.reason}`)
                .join(', ');
              return Promise.reject(reasons);
            }
            if (error.response.data.message) {
              return Promise.reject(error.response.data.message);
            }
          }
          return Promise.reject(error);
        }
      );
    },
    registerWithTotp({ commit }, user) {
      return AuthService.registerWithTotp(user).then(
        (response) => {
          commit('registerSuccess');
          return Promise.resolve(response.data);
        },
        (error) => {
          if (error.response.status === 422) {
            if (error.response.data.reasons) {
              const reasons = error.response.data.reasons
                .map((x) => `${x.field} ${x.reason}`)
                .join(', ');
              return Promise.reject(reasons);
            }
            if (error.response.data.message) {
              return Promise.reject(error.response.data.message);
            }
          }
          return Promise.reject(error);
        }
      );
    },
    verify({ commit }, user) {
      commit('setIsBusy', true, { root: true });
      return AuthService.verify(user).then(
        (response) => {
          commit('setIsBusy', false, { root: true });
          return Promise.resolve(response.data);
        },
        (error) => {
          commit('setIsBusy', false, { root: true });
          if (error.response.status === 422) {
            if (error.response.data.reasons) {
              const reasons = error.response.data.reasons
                .map((x) => `${x.field} ${x.reason}`)
                .join(', ');
              return Promise.reject(reasons);
            }
            if (error.response.data.message) {
              return Promise.reject(error.response.data.message);
            }
          }
          return Promise.reject(error);
        }
      );
    },
    verifyTotp({ commit }, payload) {
      return AuthService.verifyTotp(payload)
        .then((resp) => {
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
    verifyTotpRegistration({ commit }, payload) {
      return AuthService.verifyTotpRegistration(payload)
        .then((resp) => {
          commit('registerSuccess');
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
    sendResetCode({ commit }, payload) {
      return AuthService.sendResetCode(payload)
        .then((resp) => {
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
    resetPassword({ commit }, payload) {
      return AuthService.resetPassword(payload)
        .then((resp) => {
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
    requestTotp({ commit }, payload) {
      return AuthService.requestTotp(payload)
        .then((resp) => {
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
    completeTotp({ commit }, payload) {
      return AuthService.completeTotp(payload)
        .then((resp) => {
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
    requestSms({ commit }, payload) {
      return AuthService.requestSms(payload)
        .then((resp) => {
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
    completeSms({ commit }, payload) {
      return AuthService.completeSms(payload)
        .then((resp) => {
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },    
    refresh({ commit }, payload) {
      return AuthService.refresh(payload)
        .then((resp) => {
          commit('loginSuccess', resp);
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
    completeReset({ commit }, payload) {
      return AuthService.completeReset(payload)
        .then((resp) => {
          return Promise.resolve(resp);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    },
  },
  mutations: {
    loginSuccess(state, loginResult) {
      state.status.loggedIn = true;
      state.account = loginResult.model;
    },
    loginFailure(state) {
      state.status.loggedIn = false;
      state.account = null;
    },
    logout(state) {
      state.status.loggedIn = false;
      state.account = null;
    },
    registerSuccess(state) {
      state.status.loggedIn = false;
    },
    registerFailure(state) {
      state.status.loggedIn = false;
    },
    setAppData(state, newState) {
      state.appData = newState;
    },
    setSmsCode(state, newState) {
      state.smsCode = newState;
    },
    setVerificationCode(state, newState) {
      state.verificationCode = newState;
    },
  },
};
